import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import {
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  Clock,
  MapPin,
  Plus,
  Search,
} from 'lucide-react';
import ApplicantFilters from './ApplicantFilters/ApplicantFilters';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import Pro from 'components/Badges/Pro/Pro';
import InTalentPoolIcon from 'components/Shared/TalentPool/InTalentPoolIcon';
import styles from './ApplicantList.module.scss';

const ApplicantList = ({
  applicants,
  selectedApplicantId,
  onSelectApplicant,
  showInviteMoreButton,
  browseURL = '/browse',
  activeTab,
  unreadFilter,
  onToggleUnreadFilter,
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  availableLocations,
  availableSkills,
  sortOrder,
  onSortOrderChange,
  selectedApplicantIds = [],
  onToggleSelection,
  onSelectAll,
  showSelectAll = false,
}) => {
  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const createdAt = new Date(date);
    const diffInSeconds = Math.floor((now - createdAt) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
  };

  const isAllSelected =
    applicants.length > 0 && selectedApplicantIds.length === applicants.length;
  const isSomeSelected =
    selectedApplicantIds.length > 0 &&
    selectedApplicantIds.length < applicants.length;

  return (
    <div className={styles.listContainer}>
      <div className={styles.filterContainer}>
        {activeTab === 'applied' && (
          <button
            type="button"
            onClick={onToggleUnreadFilter}
            className={`${styles.unreadFilterButton} ${
              unreadFilter ? styles.unreadFilterActive : ''
            }`}
          >
            <span className={styles.unreadFilterLabel}>Unread only</span>
            <span
              className={`${styles.unreadFilterSwitch} ${
                unreadFilter ? styles.unreadFilterSwitchOn : ''
              }`}
            />
          </button>
        )}
        <div className={styles.searchInputWrapper}>
          <div className={styles.searchIcon}>
            <Search width={16} height={16} />
          </div>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by name, location, skills, role..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <div className={styles.listFiltersContainer}>
          <ApplicantFilters
            filters={filters}
            onFiltersChange={onFiltersChange}
            variant="list"
            availableLocations={availableLocations}
            availableSkills={availableSkills}
          />
          <button
            type="button"
            className={styles.sortToggleButton}
            onClick={() =>
              onSortOrderChange &&
              onSortOrderChange(sortOrder === 'newest' ? 'oldest' : 'newest')
            }
            title={sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
          >
            {sortOrder === 'newest' ? (
              <ArrowDownNarrowWide
                width={16}
                height={16}
                className={styles.sortToggleIcon}
              />
            ) : (
              <ArrowUpNarrowWide
                width={16}
                height={16}
                className={styles.sortToggleIcon}
              />
            )}
            <span className={styles.sortToggleLabel}>
              {sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
            </span>
          </button>
        </div>
      </div>

      {showSelectAll &&
        onSelectAll &&
        onToggleSelection &&
        applicants.length > 0 && (
          <div className={styles.selectAllContainer}>
            <label className={styles.selectAllLabel}>
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={(input) => {
                  if (input) {
                    input.indeterminate = isSomeSelected;
                  }
                }}
                onChange={onSelectAll}
                className={styles.checkbox}
              />
              <span className={styles.selectAllText}>
                {isAllSelected
                  ? `All ${applicants.length} selected`
                  : isSomeSelected
                  ? `${selectedApplicantIds.length} of ${applicants.length} selected`
                  : `Select all ${applicants.length}`}
              </span>
            </label>
          </div>
        )}

      {applicants.map((applicant, index) => (
        <button
          key={applicant.id}
          onClick={() => onSelectApplicant(applicant)}
          data-walkthrough={
            activeTab === 'applied' && index === 0 ? 'applicant-card' : undefined
          }
          className={`${styles.applicantCard} ${
            selectedApplicantId === applicant.id ? styles.selected : ''
          } ${
            selectedApplicantIds.includes(applicant.id)
              ? styles.bulkSelected
              : ''
          }`}
        >
          <div className={styles.cardContent}>
            {onToggleSelection && (
              <div
                className={styles.checkboxWrapper}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                role="presentation"
              >
                <input
                  type="checkbox"
                  checked={selectedApplicantIds.includes(applicant.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleSelection(applicant.id);
                  }}
                  className={styles.checkbox}
                />
              </div>
            )}
            {/* Avatar with heart indicator */}
            <div className={styles.avatarWrapper}>
              <Thumbnail
                thumbnails={applicant.avatars}
                className={styles.avatar}
                circle
              />
              <InTalentPoolIcon
                user={applicant.user}
                size={'14'}
                className={styles.avatarHeartIndicator}
              />
            </div>

            {/* Content */}
            <div className={styles.details}>
              <div className={styles.nameRow}>
                <div className={styles.nameContainer}>
                  <h3 className={styles.name}>{applicant.name}</h3>
                  <Pro
                    user={applicant.user}
                    style={{
                      display: 'inline',
                      fontSize: '10px',
                      position: 'relative',
                    }}
                  />
                </div>
                {(applicant.isUnread || applicant.hasUnreadMessages) && (
                  <span className={styles.unreadDot} />
                )}
              </div>

              <div className={styles.location}>
                <MapPin width={12} height={12} />
                <span>{applicant.location}</span>
              </div>

              <div className={styles.roles}>
                {applicant.roles.slice(0, 3).map((role, index) => (
                  <span key={index} className={styles.roleTag}>
                    {role}
                  </span>
                ))}
              </div>

              <div className={styles.footer}>
                <span className={styles.timeAgo}>
                  <Clock width={12} height={12} />
                  {formatTimeAgo(applicant.timeAgo)}
                </span>
              </div>
            </div>
          </div>
        </button>
      ))}

      {applicants.length === 0 && (
        <div className={styles.emptyState}>
          <p>
            {searchQuery
              ? 'No applicants match your search'
              : 'No applicants found'}
          </p>
        </div>
      )}

      {showInviteMoreButton && applicants.length > 0 && (
        <Button href={browseURL} className={styles.inviteMoreButton}>
          <Plus width={24} height={24} />
          <span>Invite more experts</span>
        </Button>
      )}
    </div>
  );
};

ApplicantList.propTypes = {
  applicants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      avatars: PropTypes.shape({
        images: PropTypes.object,
      }),
      location: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.string),
      skills: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
        })
      ),
      timeAgo: PropTypes.string,
    })
  ).isRequired,
  selectedApplicantId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onSelectApplicant: PropTypes.func.isRequired,
  showInviteMoreButton: PropTypes.bool,
  browseURL: PropTypes.string,
  activeTab: PropTypes.string,
  unreadFilter: PropTypes.bool,
  onToggleUnreadFilter: PropTypes.func,
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func,
  filters: PropTypes.shape({
    sortOrder: PropTypes.oneOf(['newest', 'oldest']),
    selectedLocations: PropTypes.arrayOf(PropTypes.string),
    selectedSkills: PropTypes.arrayOf(PropTypes.string),
  }),
  onFiltersChange: PropTypes.func,
  sortOrder: PropTypes.oneOf(['newest', 'oldest']),
  onSortOrderChange: PropTypes.func,
  availableLocations: PropTypes.arrayOf(
    PropTypes.shape({
      country: PropTypes.string.isRequired,
      cities: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          count: PropTypes.number.isRequired,
        })
      ),
      count: PropTypes.number.isRequired,
    })
  ),
  availableSkills: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    })
  ),
  selectedApplicantIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onToggleSelection: PropTypes.func,
  onSelectAll: PropTypes.func,
  showSelectAll: PropTypes.bool,
};

ApplicantList.defaultProps = {
  selectedApplicantId: null,
  showInviteMoreButton: false,
  browseURL: '/browse',
  searchQuery: '',
  onSearchChange: () => {},
  sortOrder: 'newest',
  onSortOrderChange: () => {},
  selectedApplicantIds: [],
  showSelectAll: false,
};

export default ApplicantList;
